import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, CircleCheck } from 'lucide-react';
import { useNavigate, getRouteApi } from '@tanstack/react-router';
import { Alert } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import { authService } from '../../services/authService';
import { getBrandAsset } from '@/themes';
import { validatePassword, getPasswordHelperText, PASSWORD_REQUIREMENTS } from '../../utils/passwordValidation';

const routeApi = getRouteApi('/reset-password');

const ResetPassword: React.FC = () => {
  const searchParams = routeApi.useSearch();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Extract email and token from URL parameters
    const emailParam = searchParams.email;
    const tokenParam = searchParams.token;

    if (!emailParam || !tokenParam) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    setEmail(emailParam);
    setToken(tokenParam);
  }, [searchParams.email, searchParams.token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authService.resetPassword(email, token, newPassword);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate({ to: '/login' });
      }, 3000);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred while resetting your password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate({ to: '/login' });
  };

  if (!email || !token) {
    return (
      <div className="mx-auto max-w-screen-sm px-4">
        <div className="mt-16 flex flex-col items-center">
          <Card className="w-full max-w-[400px]">
            <CardContent className="p-8 pt-8 text-center">
              <h1 className="mb-2 text-2xl font-semibold text-destructive">
                Invalid Reset Link
              </h1>
              <p className="mb-4 text-muted-foreground">
                This password reset link is invalid or has expired. Please request a new password reset.
              </p>
              <Button
                onClick={handleBackToLogin}
                className="mt-2"
              >
                Back to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="mx-auto max-w-screen-sm px-4">
        <div className="mt-16 flex flex-col items-center">
          <Card className="w-full max-w-[400px]">
            <CardContent className="p-8 pt-8 text-center">
              <CircleCheck className="mx-auto mb-4 h-16 w-16 text-green-600" />
              <h1 className="mb-2 text-2xl font-semibold text-green-600">
                Password Reset Successful!
              </h1>
              <p className="mb-4 text-muted-foreground">
                Your password has been successfully reset. You will be redirected to the login page shortly.
              </p>
              <Button
                onClick={handleBackToLogin}
                className="mt-2"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen-sm px-4">
      <div className="mt-16 flex flex-col items-center">
        <Card className="w-full max-w-[400px]">
          <CardContent className="p-8 pt-8">
            {/* Header */}
            <div className="mb-6 flex flex-col items-center">
              <img
                src={getBrandAsset('logo.svg')}
                alt="Logo"
                style={{
                  width: 'auto',
                  maxWidth: '180px',
                  height: '60px',
                  marginBottom: '16px'
                }}
              />
              <h1 className="mb-2 text-center text-2xl font-semibold">
                Reset Your Password
              </h1>
              <p className="text-center text-sm text-muted-foreground">
                Enter your new password below
              </p>
            </div>

            {error && (
              <Alert severity="error" className="mb-4">
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <p className="mb-4 text-sm text-muted-foreground">
                Resetting password for: <strong>{email}</strong>
              </p>

              <div className="mt-4 space-y-1">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                    required
                    className="pr-9"
                  />
                  <button
                    type="button"
                    aria-label="toggle password visibility"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    disabled={isLoading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground disabled:pointer-events-none disabled:opacity-50"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">{getPasswordHelperText(newPassword)}</p>
              </div>

              <div className="mt-4 space-y-1">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    required
                    className="pr-9"
                  />
                  <button
                    type="button"
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isLoading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground disabled:pointer-events-none disabled:opacity-50"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword !== '' && newPassword !== confirmPassword && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
              </div>

              <Button
                type="submit"
                className="mt-6 mb-4 w-full py-3"
                disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              >
                {isLoading ? <InlineSpinner /> : <Lock className="h-4 w-4" />}
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="mt-1 w-full"
                onClick={handleBackToLogin}
                disabled={isLoading}
              >
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
